import { IonButton } from '@ionic/react';
import React, { useState } from 'react';
import { once, run } from '../../helper/electron';
import { usePlanAware } from '../../hooks/usePlanAware';
import { useDispatch } from '../../store/hooks';
import {
  setPreferencesActiveSection,
  setShowPreferences,
} from '../../store/slices/preferences.slice';
import { ChDialog } from '../common/ChDialog/ChDialog';
import { useToast } from '../ToastManager/useToast';

interface DownloadUrlResult {
  url: string;
  fileName?: string;
  accessToken?: string;
}

interface DirectDownloadButtonProps {
  mediaId: string;
  getDownloadUrl: (
    mediaId: string,
    authOrKeywords: string,
  ) => Promise<DownloadUrlResult>;
  downloadFolder: string;
  onDownloaded: (filePath: string) => void;
  passedFileName?: string;
  keywords?: string;
  auth?: string;
}

export const DirectDownloadButton: React.FC<DirectDownloadButtonProps> = ({
  mediaId,
  getDownloadUrl,
  downloadFolder,
  onDownloaded,
  passedFileName,
  keywords = '',
  auth,
}) => {
  const [busy, setBusy] = useState(false);
  const [showDownloadFolderDialog, setShowDownloadFolderDialog] =
    useState(false);
  const dispatch = useDispatch();

  const { errorToast } = useToast();

  const planAwareDownloadUrl = usePlanAware(getDownloadUrl);

  const openDownloadFolderPreferences = () => {
    dispatch(setPreferencesActiveSection('service-integrations'));
    dispatch(setShowPreferences(true));
  };

  const handleClick = async () => {
    if (!downloadFolder) {
      setShowDownloadFolderDialog(true);
      return;
    }
    setBusy(true);
    try {
      const {
        url,
        fileName: returnedFileName,
        accessToken,
      } = await planAwareDownloadUrl(mediaId, auth || keywords);

      // pick the fileName from the response or fallback to the prop
      const fileName = returnedFileName ?? passedFileName;
      if (!fileName) {
        throw new Error(
          'No fileName provided by the API and no passedFileName was set.',
        );
      }
      run('downloadFile', { url, fileName, downloadFolder, accessToken });
      once(`downloadFile:${fileName}`, async (data: any) => {
        onDownloaded(data.filePath);
        setBusy(false);
      });
    } catch (err: any) {
      setBusy(false);

      // Check if this is an LNRP-related error (404 or 502)
      const statusCode = err?.response?.status || err?.status;
      if (statusCode === 404 || statusCode === 502) {
        errorToast({
          message:
            'Sorry. We are having troubles connecting to LNRP for this track. We have been notified. Please click on the track to go directly to the website',
          duration: 5000,
        });
      } else {
        console.error('Download failed:', err);
      }
    }
  };

  return (
    <>
      <IonButton
        fill="clear"
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          handleClick();
        }}
        disabled={busy}
      >
        {busy ? 'Downloading…' : 'Add to Crate'}
      </IonButton>

      <ChDialog
        isOpen={showDownloadFolderDialog}
        onDismiss={() => setShowDownloadFolderDialog(false)}
        title="Download folder required"
        message="To add tracks to your crate, set a download folder path in Preferences. Choose where downloaded files are saved on your computer."
        backdropDismiss
        buttons={[
          {
            text: 'Cancel',
            role: 'cancel',
            variant: 'secondary',
          },
          {
            text: 'Set path',
            variant: 'primary',
            handler: openDownloadFolderPreferences,
          },
        ]}
      />
    </>
  );
};
